import { useTranslation } from 'react-i18next';
import type { TaskState, TaskSummary } from '../lib/api';
import { scheduleLine } from '../lib/schedule-line';
import { titleLabel } from '../lib/activity';
import { formatMoney, relativeWithAbsolute, zoneLabel } from '../lib/format';
import { useMaybeSession } from '../lib/session';
import { Badge, type AutonomyLevel } from './ui/Badge';
import { Bdi, Isolated } from './ui/Bdi';
import { ReconnectAction } from './ui/ReconnectAction';
import { Check, Close, Spinner } from './ui/icons';

/**
 * Autonomy level TWO holds for a task, until tasks carry their own policy decision:
 * awaiting approval or high/critical risk → asks first; medium → prepares; low → auto; none → observes.
 */
export function autonomyForTask(
  task: Pick<TaskSummary, 'state' | 'risk_tier' | 'level'>,
): AutonomyLevel {
  // M4 tasks carry the level TWO actually held.
  if (task.level) return task.level;
  if (task.state === 'awaiting_approval') return 'ask';
  switch (task.risk_tier) {
    case 'critical':
    case 'high':
      return 'ask';
    case 'medium':
      return 'prepare';
    case 'low':
      return 'auto';
    default:
      return 'observe';
  }
}

/**
 * Done lists only executed tasks; failed, cancelled and prepared-only ones need the person
 * (M4.5, M5.1).
 */
export const TASK_SEGMENTS = {
  active: ['understood', 'planned', 'executing', 'verifying', 'compensating', 'unknown'],
  waiting: ['awaiting_approval', 'waiting'],
  attention: ['failed', 'cancelled', 'prepared'],
  done: ['done'],
} as const satisfies Record<string, readonly TaskState[]>;
export type TaskSegment = keyof typeof TASK_SEGMENTS;

export function segmentOf(state: TaskState): TaskSegment {
  if ((TASK_SEGMENTS.waiting as readonly string[]).includes(state)) return 'waiting';
  if ((TASK_SEGMENTS.attention as readonly string[]).includes(state)) return 'attention';
  if ((TASK_SEGMENTS.done as readonly string[]).includes(state)) return 'done';
  return 'active';
}

/** A finished task never shows a running spinner: its steps take the task's final state. */
export function stepStatus(
  step: { status: string },
  taskState: TaskState,
): 'running' | 'done' | 'failed' | 'pending' | 'skipped' {
  const finished = segmentOf(taskState) === 'done' || segmentOf(taskState) === 'attention';
  if (!finished) return step.status as never;
  if (step.status === 'running' || step.status === 'pending') {
    return taskState === 'done' || taskState === 'prepared'
      ? 'done'
      : taskState === 'failed'
        ? 'failed'
        : 'skipped';
  }
  return step.status as never;
}

/**
 * M5.3: an event chip reads on two lines: the title, then date · time · zone. The server label
 * is "title · Thu 24 Sep, 15:00–16:00 (Asia/Qatar)".
 */
export function eventChipLines(label: string, locale: string): { title: string; when?: string } {
  const i = label.indexOf(' · ');
  if (i < 0) return { title: label };
  const title = label.slice(0, i);
  const rest = label.slice(i + 3);
  const m = /^(.*?)\s*\(([^)]+)\)\s*$/u.exec(rest);
  const when = m
    ? `${(m[1] ?? '').replace(/[,،]\s*/gu, ' · ')} · ${zoneLabel(m[2] ?? '', locale)}`
    : rest.replace(/[,،]\s*/gu, ' · ');
  return { title, when };
}

/** A reminder is TWO's own clock, not a level of autonomy: no badge on its card. */
export function isReminderTask(task: Pick<TaskSummary, 'intent' | 'title'>): boolean {
  return task.intent === 'reminder' || task.title?.key === 'reminder_set';
}

export function TaskCard({
  task,
  children,
  onCancelSchedule,
  cancelledLine,
}: {
  task: TaskSummary;
  children?: React.ReactNode;
  /** M5: end the reminder, monitor or brief behind this task. */
  onCancelSchedule?: ((scheduleId: string) => void) | undefined;
  /** "Cancelled. Nothing was changed." after the person cancelled it here. */
  cancelledLine?: string | undefined;
}) {
  const { t, i18n } = useTranslation();
  const me = useMaybeSession()?.me;
  const locale = i18n.resolvedLanguage ?? 'en';
  const lang: 'en' | 'ar' = locale === 'ar' ? 'ar' : 'en';
  const timeZone = me?.timezone ?? 'Asia/Qatar';
  const segment = segmentOf(task.state);
  // M5.2: the authority is a key rendered in the UI language; older rows keep their text.
  const authority = task.authority_key
    ? t(`authority.${task.authority_key}`, { defaultValue: task.authority ?? '' })
    : task.authority;
  const stateTone =
    segment === 'waiting'
      ? 'bg-attention-soft text-attention-text'
      : task.state === 'failed'
        ? 'bg-critical-soft text-critical-text'
        : task.state === 'cancelled' || task.state === 'prepared'
          ? 'bg-surface-2 text-ink-2'
          : segment === 'done'
            ? 'bg-success-soft text-success-text'
            : 'bg-brand-soft text-ink';

  return (
    <article className="rounded-lg border border-line bg-surface p-4 shadow-sm dark:bg-elevated">
      <div className="flex items-start justify-between gap-3">
        <h3 className="text-base font-medium text-ink" dir="auto">
          {task.title ? (
            <>
              <span>{titleLabel(task.title, t)}</span>
              {task.title.subject ? (
                <>
                  <span>: «</span>
                  <bdi dir="auto">
                    <Isolated text={task.title.subject} />
                  </bdi>
                  <span>»</span>
                </>
              ) : null}
            </>
          ) : (
            <Isolated text={task.summary ?? task.intent} />
          )}
        </h3>
        <span
          className={['shrink-0 rounded-full px-2 py-0.5 text-xs font-medium', stateTone].join(' ')}
        >
          {t(`tasks.state.${task.state}`)}
        </span>
      </div>

      {task.steps.length ? (
        <ol className="mt-3 flex flex-col gap-1.5">
          {task.steps.map((s) => {
            const status = stepStatus(s, task.state);
            return (
              <li key={s.step_id} className="flex flex-col gap-0.5 text-sm text-ink">
                <span className="flex items-center gap-2">
                  {status === 'running' ? (
                    <Spinner size={14} className="text-brand" />
                  ) : status === 'done' ? (
                    <Check size={14} className="text-success" strokeWidth={2.5} />
                  ) : status === 'failed' ? (
                    <Close size={14} className="text-critical" />
                  ) : (
                    <span
                      aria-hidden
                      className="ms-0.5 size-2.5 rounded-full border border-line-strong"
                    />
                  )}
                  <span
                    dir="auto"
                    className={status === 'pending' || status === 'skipped' ? 'text-ink-2' : ''}
                  >
                    <Isolated text={s.title} />
                  </span>
                </span>
                {status === 'failed' && s.description ? (
                  <span dir="auto" className="ps-6 text-xs text-critical-text" data-step-reason>
                    <Isolated text={s.description} />
                  </span>
                ) : null}
              </li>
            );
          })}
        </ol>
      ) : null}

      <div className="mt-3 flex flex-wrap items-center gap-2">
        {isReminderTask(task) ? null : <Badge level={autonomyForTask(task)} />}
        {task.connection ? (
          <span className="text-xs text-ink-2">
            {t(`connections.provider.${task.connection.provider}`)} ·{' '}
            <Bdi>{task.connection.account_email}</Bdi>
          </span>
        ) : null}
      </div>
      {task.schedule && task.schedule.status === 'active' ? (
        <div className="mt-3 flex items-center justify-between gap-3">
          <p className="tabular text-sm text-ink-2" dir="auto">
            {scheduleLine(task.schedule, lang)}
          </p>
          {onCancelSchedule ? (
            <button
              type="button"
              onClick={() => onCancelSchedule(task.schedule?.schedule_id ?? '')}
              className="min-h-10 rounded-md border border-line bg-canvas px-3 text-sm font-medium text-ink hover:bg-surface"
            >
              {t('tasks.cancelSchedule')}
            </button>
          ) : null}
        </div>
      ) : null}
      {cancelledLine ? (
        <p className="mt-3 text-sm text-ink-2" dir="auto" role="status">
          {cancelledLine}
        </p>
      ) : null}
      {task.state === 'failed' && task.error ? (
        <div className="mt-3 flex flex-col gap-2">
          <p className="text-sm text-critical-text" dir="auto">
            <Isolated text={task.error} />
          </p>
          {task.fix ? (
            <ReconnectAction provider={task.fix.provider} action={task.fix.action} />
          ) : null}
        </div>
      ) : null}
      {task.state === 'prepared' ? (
        <div className="mt-3 flex flex-col gap-2">
          {task.checklist?.length ? (
            <ul className="flex flex-col gap-1.5">
              {task.checklist.map((line) => (
                <li key={line} className="text-sm text-ink-2" dir="auto">
                  <Isolated text={line} />
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-ink-2" dir="auto">
              {t('tasks.preparedNote')}
            </p>
          )}
          <ReconnectAction
            provider={task.connection?.provider === 'microsoft' ? 'microsoft' : 'google'}
            action="permissions"
          />
        </div>
      ) : null}
      {authority ? (
        <p className="mt-2 text-xs text-ink-2" dir="auto">
          <span className="text-ink-3">{t('tasks.authority')} · </span>
          <Isolated text={authority} />
        </p>
      ) : null}
      {task.artifacts?.length ? (
        <ul className="mt-2 flex flex-wrap gap-2">
          {task.artifacts.map((a) => (
            <li key={`${a.kind}:${a.id}`} className="text-xs text-ink-2" dir="auto">
              {a.kind === 'event' && a.label ? (
                <EventChip
                  label={a.label}
                  locale={locale}
                  href={a.web_link}
                  kind={t('tasks.artifact.event')}
                />
              ) : a.web_link ? (
                <a
                  href={a.web_link}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-h-8 items-center rounded-full border border-line bg-canvas px-2.5 text-brand"
                >
                  {t(`tasks.artifact.${a.kind}`)}
                  {a.label ? <Isolated text={` · ${a.label}`} /> : null}
                </a>
              ) : (
                <span className="inline-flex min-h-8 items-center rounded-full border border-line bg-canvas px-2.5">
                  {t(`tasks.artifact.${a.kind}`)}
                  {a.label ? <Isolated text={` · ${a.label}`} /> : null}
                </span>
              )}
            </li>
          ))}
        </ul>
      ) : null}
      {task.checklist?.length && segmentOf(task.state) === 'done' ? (
        <ul className="mt-3 flex flex-col gap-1.5">
          {task.checklist.map((line) => (
            <li key={line} className="flex items-center gap-2 text-sm text-ink">
              <Check size={14} className="text-success" strokeWidth={2.5} />
              <span dir="auto">
                <Isolated text={line} />
              </span>
            </li>
          ))}
        </ul>
      ) : null}

      {children ? <div className="mt-3">{children}</div> : null}

      <div className="mt-3 flex items-center justify-between text-xs text-ink-3">
        <span className="tabular">
          {t('tasks.updated')} {relativeWithAbsolute(task.updated_at, locale, timeZone)}
        </span>
        {task.cost_usd ? (
          <span className="tabular">{formatMoney(task.cost_usd, 'USD', locale)}</span>
        ) : null}
      </div>
    </article>
  );
}

/** The event chip: the title on one line, date · time · zone on the next (M5.3). */
function EventChip({
  label,
  locale,
  href,
  kind,
}: {
  label: string;
  locale: string;
  href?: string | undefined;
  kind: string;
}) {
  const lines = eventChipLines(label, locale);
  const body = (
    <>
      <span className="block">
        <span className="text-ink-3">{kind} · </span>
        <Isolated text={lines.title} />
      </span>
      {lines.when ? (
        <span className="tabular block text-ink-3">
          <bdi dir="ltr">{lines.when}</bdi>
        </span>
      ) : null}
    </>
  );
  const className =
    'inline-flex min-h-8 flex-col items-start rounded-lg border border-line bg-canvas px-2.5 py-1';
  return href ? (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={`${className} text-brand`}
      data-event-chip
    >
      {body}
    </a>
  ) : (
    <span className={className} data-event-chip>
      {body}
    </span>
  );
}
